import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Button, Card, Chip } from '@material-ui/core'
import { Box } from '@mui/material'
import authSlice from 'store/auth'
import { getConsoleHome } from 'routes'
import AppLogo from 'assets/white_logo.png'

const ConferenceSelector = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const classes = useStyles()

  const { partialUser, assignments } = location.state || {}

  const savedId = partialUser
    ? localStorage.getItem(`selectedConferenceId_${partialUser.id}`)
    : null
  const defaultId =
    assignments?.find((a) => a.conferenceId === savedId && !a.isExpired)?.conferenceId ||
    assignments?.find((a) => !a.isExpired)?.conferenceId

  const [selectedConfId, setSelectedConfId] = useState(defaultId)

  if (!partialUser || !assignments?.length) {
    history.replace('/')
    return null
  }

  const handleConfirm = () => {
    const conf = assignments.find((a) => a.conferenceId === selectedConfId)
    const fullUser = { ...partialUser, conferenceId: selectedConfId, role: conf.role }
    localStorage.setItem(`selectedConferenceId_${partialUser.id}`, selectedConfId)
    dispatch(authSlice.actions.setLoginInProgress(false))
    dispatch(authSlice.actions.setUser(fullUser))
    dispatch(authSlice.actions.setAuthenticated(true))
    history.replace(getConsoleHome(fullUser))
  }

  return (
    <Box className={classes.root}>
      {/* ── Blurred fake-dashboard background ── */}
      <Box className={classes.blurredBg}>
        {/* Topbar */}
        <Box className={classes.fakeTopbar}>
          <Box className={classes.fakeLogoArea}>
            <img src={AppLogo} alt="" className={classes.fakeLogo} />
          </Box>
          <Box className={classes.fakeTopbarRight}>
            <Box className={classes.fakeChip} />
            <Box className={classes.fakeAvatar} />
          </Box>
        </Box>

        {/* Body: sidebar + content */}
        <Box className={classes.fakeBody}>
          {/* Sidebar */}
          <Box className={classes.fakeSidebar}>
            {[80, 60, 70, 55, 65, 50].map((w, i) => (
              <Box key={i} className={classes.fakeNavItem} style={{ width: `${w}%` }} />
            ))}
          </Box>

          {/* Content */}
          <Box className={classes.fakeContent}>
            <Box className={classes.fakeContentRow}>
              {[1, 2, 3].map((i) => (
                <Box key={i} className={classes.fakeCard} />
              ))}
            </Box>
            <Box className={classes.fakeTable}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} className={classes.fakeTableRow} />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Dark overlay ── */}
      <Box className={classes.overlay} />

      {/* ── Picker card ── */}
      <Box className={classes.cardWrapper}>
        <Card className={classes.card} elevation={0}>
          <Typography className={classes.cardTitle}>Select a Conference</Typography>
          <Typography className={classes.cardSubtitle}>
            Your account is linked to multiple conferences. Choose one to continue.
          </Typography>

          {assignments.some((a) => a.isExpired) && (
            <Typography style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
              Some conferences are unavailable because they have expired.
            </Typography>
          )}
          <Box className={classes.list}>
            {assignments.map((assignment) => {
              const isSelected = selectedConfId === assignment.conferenceId
              const isOwner = (assignment.role ?? 'admin') === 'owner'
              const role = assignment.role ?? 'admin'
              const isExpired = assignment.isExpired
              return (
                <Box
                  key={assignment.conferenceId}
                  onClick={() => !isExpired && setSelectedConfId(assignment.conferenceId)}
                  className={`${classes.confRow} ${isSelected ? classes.confRowSelected : ''} ${
                    isExpired ? classes.confRowExpired : ''
                  }`}>
                  <Typography
                    className={classes.confName}
                    style={isExpired ? { color: '#94a3b8' } : {}}>
                    {assignment.confName}
                  </Typography>
                  <Box style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                    <Chip
                      label={role.charAt(0).toUpperCase() + role.slice(1)}
                      size="small"
                      style={{
                        backgroundColor: isOwner ? '#dbeafe' : '#f1f5f9',
                        color: isOwner ? '#1d4ed8' : '#475569',
                        fontWeight: 700,
                        fontSize: 11,
                        height: 22,
                        opacity: isExpired ? 0.5 : 1
                      }}
                    />
                    {isExpired && (
                      <Chip
                        label="Expired"
                        size="small"
                        style={{
                          backgroundColor: '#fef2f2',
                          color: '#ef4444',
                          fontWeight: 700,
                          fontSize: 11,
                          height: 22
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={
              !selectedConfId ||
              assignments?.find((a) => a.conferenceId === selectedConfId)?.isExpired
            }
            onClick={handleConfirm}
            className={classes.continueBtn}>
            Continue
          </Button>
        </Card>
      </Box>
    </Box>
  )
}

export default ConferenceSelector

const useStyles = makeStyles({
  root: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  // ── Blurred background ──
  blurredBg: {
    position: 'absolute',
    inset: 0,
    filter: 'blur(6px)',
    transform: 'scale(1.04)',
    pointerEvents: 'none',
    userSelect: 'none'
  },
  fakeTopbar: {
    height: 64,
    background: 'linear-gradient(135deg, #22b1f3 0%, #15b4db 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 16,
    paddingRight: 24
  },
  fakeLogoArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  fakeLogo: {
    height: 36,
    opacity: 0.9,
    objectFit: 'contain'
  },
  fakeTopbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },
  fakeChip: {
    width: 100,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.25)'
  },
  fakeAvatar: {
    width: 80,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.2)'
  },
  fakeBody: {
    display: 'flex',
    height: 'calc(100vh - 64px)'
  },
  fakeSidebar: {
    width: 220,
    backgroundColor: '#ffffff',
    borderRight: '1px solid #e2e8f0',
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  fakeNavItem: {
    height: 36,
    borderRadius: 8,
    backgroundColor: '#f1f5f9'
  },
  fakeContent: {
    flex: 1,
    backgroundColor: '#f8fafc',
    padding: 28,
    display: 'flex',
    flexDirection: 'column',
    gap: 24
  },
  fakeContentRow: {
    display: 'flex',
    gap: 20
  },
  fakeCard: {
    flex: 1,
    height: 100,
    borderRadius: 12,
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0'
  },
  fakeTable: {
    borderRadius: 12,
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  fakeTableRow: {
    height: 52,
    borderBottom: '1px solid #f1f5f9',
    margin: '0 20px',
    '&:last-child': { borderBottom: 'none' }
  },
  // ── Overlay ──
  overlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.55)'
  },
  // ── Picker card ──
  cardWrapper: {
    position: 'relative',
    zIndex: 10,
    width: '100%',
    maxWidth: 480,
    padding: '0 16px'
  },
  card: {
    borderRadius: 20,
    padding: '36px 32px 28px',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(255,255,255,0.15)'
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: 8
  },
  cardSubtitle: {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 24,
    lineHeight: 1.5
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 28,
    maxHeight: 340,
    overflowY: 'auto',
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: 4 }
  },
  confRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '13px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: 10,
    cursor: 'pointer',
    backgroundColor: '#ffffff',
    transition: 'border-color 0.15s, background-color 0.15s',
    '&:hover': {
      borderColor: '#93c5fd',
      backgroundColor: '#f8fafc'
    }
  },
  confRowSelected: {
    borderColor: '#15b4db !important',
    backgroundColor: '#f0f9ff !important'
  },
  confRowExpired: {
    cursor: 'not-allowed !important',
    backgroundColor: '#f8fafc !important',
    borderColor: '#e2e8f0 !important',
    '&:hover': {
      borderColor: '#e2e8f0 !important',
      backgroundColor: '#f8fafc !important'
    }
  },
  confName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1e293b'
  },
  continueBtn: {
    borderRadius: 10,
    padding: '12px 0',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: 15,
    background: 'linear-gradient(135deg, #22b1f3 0%, #15b4db 100%)',
    boxShadow: '0 4px 14px rgba(21,180,219,0.35)',
    '&:hover': {
      boxShadow: '0 6px 18px rgba(21,180,219,0.5)'
    }
  }
})
